import { useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";

import { Select } from "@/components/Select/Select";
import { BarChart } from "@/modules/analytics/components/BarChart";
import { NoData } from "@/modules/analytics/components/NoData";
import { ChartLegend } from "@/modules/analytics/components/utility/ChartLegend";
import { dataColors } from "@/modules/analytics/theme";
import { type DateRangeOption, formatChartDate, trimDataOnRange } from "@/modules/analytics/util";
import { useAnalyticsQueries } from "@/queries/analytics";
import { useTranslation } from "@/translations";
import type { NonEmptyArray } from "@/types/utility-types";

import { Card, CardHeader } from "../../../components/Card";

interface SolvedMessagesCardProps {
  dateRangeOptions: NonEmptyArray<DateRangeOption>;
}
const RANGE = "lastYear";

export function SolvedMessagesCard({ dateRangeOptions }: SolvedMessagesCardProps): React.ReactNode {
  const [dateRange, setDateRange] = useState<DateRangeOption>(dateRangeOptions[0]);

  const { t } = useTranslation();
  const analyticsQueries = useAnalyticsQueries();
  const { data } = useSuspenseQuery(analyticsQueries.getAskForHelpStats(RANGE));

  const trimmedData = trimDataOnRange(data.data, dateRange);
  const primaryData = data
    ? trimmedData.map((dataPoint) => ({
        x: formatChartDate(dataPoint.range),
        y: dataPoint.askHelpCreatedCount,
        isPending: dataPoint.isCurrentMonth,
      }))
    : [];
  const secondaryData = data
    ? trimmedData.map((dataPoint) => ({
        x: formatChartDate(dataPoint.range),
        y: dataPoint.askHelpResolvedCount,
        isPending: dataPoint.isCurrentMonth,
      }))
    : [];
  const noData = data.data.length === 0;

  return (
    <Card className="h-full">
      <CardHeader
        tooltip={t("page.analytics-overview.chart.solved-messages.title-tooltip")}
        title={t("page.analytics-overview.chart.solved-messages.title")}
      >
        <Select
          items={dateRangeOptions}
          selected={dateRange}
          onChange={setDateRange}
          renderOption={(x) => x.name}
          keySelector={(x) => x.value}
        />
      </CardHeader>
      {noData ? (
        <NoData />
      ) : (
        <div className="flex flex-col items-center gap-4">
          <div className="w-full">
            <BarChart
              type="date"
              primaryData={{ data: primaryData }}
              secondaryData={{
                data: secondaryData,
                color: dataColors.green,
              }}
              maxY={Math.max(...primaryData.map((x) => x.y || 0), ...secondaryData.map((x) => x.y || 0)) + 10}
              formatYTick={(y) => y}
              xLabel={t("page.analytics-overview.chart.solved-messages.x-axis")}
              width={550}
              renderLabel={(dataPoint) => (
                <div className="flex flex-col">
                  <span className="flex items-center gap-2">
                    <span className="mt-0.5 size-3 rounded-sm" style={{ backgroundColor: dataColors.primary }} />
                    <span className="flex flex-1 justify-between gap-2">
                      {t("page.analytics-overview.chart.solved-messages.data-point.tooltip.primary", {
                        amount: dataPoint.primary?.y || 0,
                      })}
                    </span>
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="mt-0.5 size-3 rounded-sm" style={{ backgroundColor: dataColors.green }} />
                    <span className="flex flex-1 justify-between gap-2">
                      {t("page.analytics-overview.chart.solved-messages.data-point.tooltip.secondary", {
                        amount: dataPoint.secondary?.y || 0,
                      })}
                    </span>
                  </span>
                </div>
              )}
            />
          </div>

          <div className="w-full">
            <ChartLegend
              items={[
                {
                  label: t("page.analytics-overview.chart.solved-messages.legend.help-questions-asked"),
                  color: dataColors.primary,
                },
                {
                  label: t("page.analytics-overview.chart.solved-messages.legend.help-questions-solved"),
                  color: dataColors.green,
                },
                {
                  label: t("page.analytics-overview.chart.solved-messages.legend.help-questions-asked-current-period"),
                  color: dataColors.primary,
                  pattern: "striped",
                },
                {
                  label: t("page.analytics-overview.chart.solved-messages.legend.help-questions-solved-current-period"),
                  color: dataColors.green,
                  pattern: "striped",
                },
              ]}
            />
          </div>
        </div>
      )}
    </Card>
  );
}
